import React, { useEffect, useState } from "react";
import { Card, Typography, Input, Row, Col, Flex } from "antd";
import { toast } from "react-toastify";
import CustomLoader from "../../../../common/UI/Loader";
import ENDPOINTS from "../../../../../services/endpoint";
import apiInstance from "../../../../../services/apiService";
import SettingsSidebarHeader from "../../../../common/SettingsSidebarHeader";
import "./index.scss";
import { customLogger } from "../../../../../utils/Logger";

const { Text } = Typography;

interface ViewCardProps {
  id: string | undefined;
  handleBackToPrevious: () => void;
}

interface CardDetails {
  cardId: string;
  brand: string;
  fullName: string;
  userName: string;
  last4: string;
  masked_number: string;
  expiry_month: number;
  expiry_year: number;
  status: string;
  street: string;
  city: string;
  state: string;
  zip: string;
  country: string;
}

const ViewCard: React.FC<ViewCardProps> = ({ id, handleBackToPrevious }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [cardData, setCardData] = useState<CardDetails>();

  const getCardData = async () => {
    const url = ENDPOINTS.GET_CARD + "/" + id;

    setLoading(true);
    try {
      const response = await apiInstance.get(url);
      const data = response.data.cardDetails;
      setCardData(data);
    } catch (error: any) {
      customLogger(error);
      toast.error(
        error.message || "There was an error fetching Card information"
      );
    } finally {
      setLoading(false);
    }
  };

  const getValidThroughDate = (
    month: number | undefined,
    year: number | undefined
  ) => {
    if (!month || !year) return;

    const formattedMonth = month < 10 ? `0${month}` : `${month}`;
    const formattedYear = year.toString().slice(-2);
    return `${formattedMonth}/${formattedYear}`;
  };

  useEffect(() => {
    getCardData();
  }, []);

  return (
    <div className="view-card-page">
      <SettingsSidebarHeader
        title="Add Card"
        handleBackClick={handleBackToPrevious}
      />

      {loading ? (
        <Flex className="w-full h-full" justify="center" align="center">
          <CustomLoader />
        </Flex>
      ) : (
        <div className="card-details-container">
          <Card className="credit-card" styles={{ body: { padding: 0 } }}>
            <div className="credit-card__content">
              <img
                src="/assets/images/chip.png"
                alt="Chip"
                className="credit-card__chip"
              />
              <div className="credit-card__number">
                **** **** **** {cardData?.last4}
              </div>
              <div className="credit-card__label">CARD NAME</div>
              <div className="credit-card__name">{cardData?.fullName}</div>
              <div className="credit-card__valid">
                <span>VALID THROUGH</span>
                <span className="credit-card__expiry">
                  {getValidThroughDate(
                    cardData?.expiry_month,
                    cardData?.expiry_year
                  )}
                </span>
              </div>
            </div>
          </Card>

          <div className="form-section">
            <Row gutter={16}>
              <Col span={24}>
                <Text className="form-section__label">Card Number</Text>
                <Input
                  value={`**** **** **** ${cardData?.last4}`}
                  readOnly
                  className="form-section__input"
                />
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={24}>
                <Text className="form-section__label">Name on Card</Text>
                <Input
                  value={cardData?.fullName}
                  readOnly
                  className="form-section__input"
                />
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Text className="form-section__label">Expiry Date</Text>
                <Input
                  value={getValidThroughDate(
                    cardData?.expiry_month,
                    cardData?.expiry_year
                  )}
                  readOnly
                  className="form-section__input"
                />
              </Col>
              <Col span={12}>
                <Text className="form-section__label">CVV code</Text>
                <Input value="***" readOnly className="form-section__input" />
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Text className="form-section__label">State</Text>
                <Input
                  value={cardData?.state}
                  readOnly
                  className="form-section__input"
                />
              </Col>
              <Col span={12}>
                <Text className="form-section__label">City</Text>
                <Input
                  value={cardData?.city}
                  readOnly
                  className="form-section__input"
                />
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={24}>
                <Text className="form-section__label">Street</Text>
                <Input
                  value={cardData?.street}
                  readOnly
                  className="form-section__input"
                />
              </Col>
            </Row>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewCard;
